import { FileUp, X } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { formatFileSize, useCSVReader } from 'react-papaparse';

import { Button, Text } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

export type ColumnGroup = {
  columns: string[];
  name: string;
};

interface Props<T> {
  onUploadedFileRead: (results: T[]) => void;
  onRemoveFile?: () => void;
  customButton?: ReactNode;
  requiredColumns?: string[];
  optionalColumns?: string[];
  requiredGroups?: ColumnGroup[];
  externalError?: string | null;
  helperText?: ReactNode;
}

export default function UploadCsv<T>({
  onUploadedFileRead,
  onRemoveFile,
  customButton,
  requiredColumns,
  optionalColumns,
  requiredGroups,
  externalError,
  helperText,
}: Props<T>) {
  const { CSVReader } = useCSVReader();
  const [zoneHover, setZoneHover] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Show either internal or external error
  const errorMessage = externalError || fileError;

  const validateColumns = (headers: string[]): { valid: boolean; error?: string } => {
    // Check required columns
    if (requiredColumns && requiredColumns.length > 0) {
      const missingRequired = requiredColumns.filter((col) => !headers.includes(col));
      if (missingRequired.length > 0) {
        return {
          valid: false,
          error: `Missing required columns: ${missingRequired.join(', ')}`,
        };
      }
    }

    // Check required groups
    if (requiredGroups && requiredGroups.length > 0) {
      for (const group of requiredGroups) {
        const columnsInGroup = group.columns.filter((col) => headers.includes(col));

        if (columnsInGroup.length > 0 && columnsInGroup.length < group.columns.length) {
          const missing = group.columns.filter((col) => !headers.includes(col));
          return {
            valid: false,
            error: `Incomplete group "${group.name}". Missing: ${missing.join(', ')}`,
          };
        }
      }
    }

    return { valid: true };
  };

  return (
    <CSVReader
      config={{
        header: true,
        skipEmptyLines: true,
      }}
      onUploadAccepted={(results: any) => {
        setFileError(null);

        if (!results.data || results.data.length === 0) {
          setFileError('The CSV file is empty.');
          return;
        }

        const headers = Object.keys(results.data[0]);
        const validation = validateColumns(headers);

        if (!validation.valid) {
          setFileError(validation.error || 'Invalid CSV format');
          return;
        }

        const allAllowedColumns = [...(requiredColumns || []), ...(optionalColumns || [])];

        let processedData;

        if (allAllowedColumns.length > 0) {
          processedData = results.data.map((row: any) => {
            const newRow: any = {};
            headers.forEach((key: string) => {
              if (allAllowedColumns.includes(key)) {
                newRow[key] = row[key];
              }
            });
            return newRow;
          });
        } else {
          processedData = results.data;
        }

        onUploadedFileRead(processedData);
        setZoneHover(false);
      }}
      onDragOver={(event: DragEvent) => {
        event.preventDefault();
        setZoneHover(true);
      }}
      onDragLeave={(event: DragEvent) => {
        event.preventDefault();
        setZoneHover(false);
      }}
    >
      {({ getRootProps, acceptedFile, ProgressBar, getRemoveFileProps }: any) => (
        <>
          <div
            {...getRootProps()}
            className={cn(
              'tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:h-full tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:p-5 tw:transition-colors',
              zoneHover && 'tw:border-muted-foreground tw:bg-muted/50',
            )}
          >
            {acceptedFile ? (
              <>
                <div className="tw:relative tw:z-10 tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-[120px] tw:w-[120px] tw:rounded-2xl tw:bg-muted">
                  <div className="tw:flex tw:flex-col tw:items-center tw:px-2.5">
                    <span className="tw:flex tw:justify-center tw:rounded-sm tw:bg-background/40 tw:mb-2 tw:text-xs">
                      {formatFileSize(acceptedFile.size)}
                    </span>
                    <span className="tw:rounded-sm tw:bg-background/40 tw:text-xs tw:mb-2 tw:truncate tw:max-w-[100px]">
                      {acceptedFile.name}
                    </span>
                  </div>
                  <div className="tw:absolute tw:bottom-3.5 tw:w-full tw:px-2.5">
                    <ProgressBar />
                  </div>
                  <button
                    {...getRemoveFileProps()}
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      setFileError(null);
                      onRemoveFile?.();
                      const removeProps = getRemoveFileProps();
                      if (removeProps.onClick) {
                        removeProps.onClick(event);
                      }
                    }}
                    className="tw:absolute tw:right-1.5 tw:top-1.5 tw:h-[23px] tw:w-[23px] tw:flex tw:items-center tw:justify-center tw:rounded-full tw:bg-destructive/10 tw:text-destructive tw:hover:bg-destructive/20 tw:transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
                {errorMessage && (
                  <Text variant="body-md" color="error" weight="bold" className="tw:mt-4">
                    {errorMessage}
                  </Text>
                )}
                {!errorMessage && customButton && <div className="tw:mt-8">{customButton}</div>}
              </>
            ) : (
              <div className="tw:flex tw:flex-col tw:gap-6 tw:items-center">
                <FileUp size={48} className="tw:text-muted-foreground/50" />
                <div className="tw:flex tw:flex-col tw:text-center">
                  <Text variant="body-lg">Drag and drop your file here</Text>
                  <Text variant="body-md" color="muted">
                    Support only for CSV files
                  </Text>
                  {errorMessage && (
                    <Text variant="body-md" color="error" weight="bold">
                      {errorMessage}
                    </Text>
                  )}
                </div>
                <Button variant="primary">Choose file</Button>
                {helperText && <div className="tw:text-center">{helperText}</div>}
              </div>
            )}
          </div>
        </>
      )}
    </CSVReader>
  );
}
